<template>
  <div
    class="mtb"
    :class="rootClasses"
    data-test="message-template-box"
    :data-slot="slotKey"
    :data-channel="channel"
    :data-invalid="hasProblem ? 'true' : 'false'"
  >
    <div class="mtb__head">
      <label class="mtb__label" :for="isEmail ? subjectId : fieldId" data-test="message-box-label">{{ label }}</label>
      <div class="mtb__badges">
        <span v-if="showEdited" class="mtb__edited" data-test="message-box-edited">
          <span class="mtb__edited-dot"></span>Edited
        </span>
        <span v-if="informational" class="mtb__pill" data-test="message-box-not-in-use">Not in use</span>
        <span v-if="showCounter" class="mtb__counter" :class="{ 'is-over': overLimit }" data-test="message-box-counter">{{ counterText }}</span>
      </div>
    </div>

    <!-- EMAIL: the Subject line sits above the message (design frames 2a-2c, 3a). -->
    <div v-if="isEmail" class="mtb__sub">
      <label class="mtb__eyebrow" :for="subjectId">Subject</label>
      <input
        :id="subjectId"
        ref="subject"
        type="text"
        class="mtb__subject"
        :class="{ 'is-invalid': subjectProblem }"
        data-test="message-box-subject"
        autocomplete="off"
        spellcheck="true"
        :value="subject"
        :readonly="locked"
        :aria-invalid="subjectProblem ? 'true' : 'false'"
        :aria-describedby="subjectProblem ? subjectMessageId : null"
        @input="onSubjectInput"
        @focus="lastField = 'subject'"
        @select="rememberSubjectCaret"
        @keyup="rememberSubjectCaret"
        @click="rememberSubjectCaret"
        @blur="rememberSubjectCaret"
      />
      <div v-if="subjectError" :id="subjectMessageId" class="mtb__msg" role="alert" data-test="message-box-subject-error">
        <svg class="mtb__msg-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
        <span>{{ subjectError }}<template v-if="isSubjectBlank"> <button type="button" class="mtb__msg-link" data-test="message-box-subject-error-reset" @click="resetSubject">Reset to default</button> or write your own.</template></span>
      </div>
      <div v-else-if="showSubjectUnknown" :id="subjectMessageId" class="mtb__msg" role="alert" data-test="message-box-subject-unknown">
        <svg class="mtb__msg-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
        <span>
          <code class="mtb__code">{{ subjectUnknownToken }}</code> {{ subjectUnknownBlurb }}<template v-if="subjectUnknownFixToken">
            Did you mean
            <button type="button" class="mtb__fix" title="Replace it" data-test="message-box-subject-fix" @click="applySubjectFix"><code class="mtb__code mtb__code--fix">{{ subjectUnknownFixToken }}</code></button>?</template>
        </span>
      </div>
      <label class="mtb__eyebrow mtb__eyebrow--message" :for="fieldId">Message</label>
    </div>

    <!-- The message IS the value (never a placeholder), so focusing the box never clears anything. -->
    <textarea
      :id="fieldId"
      ref="field"
      class="mtb__field"
      :class="{ 'is-invalid': bodyProblem }"
      data-test="message-box-field"
      rows="3"
      spellcheck="true"
      :value="value"
      :readonly="locked"
      :aria-invalid="bodyProblem ? 'true' : 'false'"
      :aria-describedby="bodyProblem ? messageId : null"
      @input="onInput"
      @focus="lastField = 'body'"
      @select="rememberCaret"
      @keyup="rememberCaret"
      @click="rememberCaret"
      @blur="rememberCaret"
    ></textarea>

    <!-- Save-time problem handed down by the page (a blank message, ...). Shown even while the box is read-only,
         because a section that is switched off can still hold a message that blocks Save. -->
    <div v-if="error" :id="messageId" class="mtb__msg" role="alert" data-test="message-box-error">
      <svg class="mtb__msg-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
      <span>{{ error }}<template v-if="isBlank"> <button type="button" class="mtb__msg-link" data-test="message-box-error-reset" @click="resetBody">Reset to default</button> or write your own.</template><template v-if="linkAddable"> <button type="button" class="mtb__msg-link" data-test="message-box-link-add" @mousedown.prevent @click="onLinkChip">Add it</button></template></span>
    </div>

    <!-- An unknown {{variable}} is flagged as you type (it still blocks Save), with a one-click fix. -->
    <div v-else-if="showUnknown" :id="messageId" class="mtb__msg" role="alert" data-test="message-box-unknown">
      <svg class="mtb__msg-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
      <span>
        <code class="mtb__code">{{ unknownToken }}</code> {{ unknownBlurb }}<template v-if="unknownFixToken">
          Did you mean
          <button type="button" class="mtb__fix" title="Replace it" data-test="message-box-fix" @click="applyFix"><code class="mtb__code mtb__code--fix">{{ unknownFixToken }}</code></button>?</template>
      </span>
    </div>

    <!-- FORM LINK READOUT (the generic `link` prop; Forms Settings): which form / packet the {{form_link}} opens, with Change. A message that holds
         the token but has no form picked says so (and counts as an issue on the page); a saved form that is gone from the LOADED list warns instead. -->
    <div
      v-if="showLinkReadout"
      class="mtb__bk-readout"
      :class="{ 'mtb__bk-readout--none': linkNoneYet }"
      :data-state="linkNoneYet ? 'none' : 'picked'"
      data-test="message-box-link-readout"
    >
      <svg class="mtb__bk-readout-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
      <span>Link opens: <strong data-test="message-box-link-label">{{ linkNoneYet ? 'no form picked yet' : linkReadoutLabel }}</strong></span>
      <span aria-hidden="true">&middot;</span>
      <button ref="lkChange" type="button" class="mtb__link" data-test="message-box-link-change" @click="openLinkPicker">{{ linkNoneYet ? 'Choose a form' : 'Change' }}</button>
    </div>
    <div v-if="showLinkWarning" class="mtb__bk-readout mtb__bk-readout--warn" role="status" data-test="message-box-link-warning">
      <svg class="mtb__bk-readout-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>
      <span>The form this link opened was deleted — pick another.</span>
      <button ref="lkChange" type="button" class="mtb__link" data-test="message-box-link-change" @click="openLinkPicker">Change</button>
    </div>

    <div v-if="showChipRow" class="mtb__chips" data-test="message-box-chips">
      <span class="mtb__chips-label">Tap to insert:</span>

      <!-- THE FORM LINK CHIP (first in the row): not a variable chip (link icon + words). One click inserts ONE token at the caret when the text has
           none, else just reopens the picker; the popover lists Packets then Forms with a search box and is keyboard driven; on a phone it is a bottom
           sheet. The choice is handed back through `link-pick`; the box stores nothing and does not interpret the `link` contract. -->
      <span v-if="showLinkChip" ref="lkWrap" class="mtb__bk mtb__lk">
        <button
          type="button"
          class="mtb__chip mtb__chip--link"
          :class="{ 'is-missing': linkMissing }"
          data-test="message-box-link-chip"
          :data-chip="linkToken"
          aria-haspopup="dialog"
          :aria-expanded="linkOpen ? 'true' : 'false'"
          @mousedown.prevent
          @click="onLinkChip"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
          {{ linkChipLabel }}<span v-if="linkRequired" class="mtb__chip-req" data-test="message-box-link-required"> (required)</span>
        </button>
        <template v-if="linkOpen">
          <div v-if="linkSheet" class="mtb__lp-scrim" data-test="message-box-link-scrim" @click="closeLinkPicker(false)"></div>
          <div
            ref="lkPop"
            class="mtb__lp"
            :class="{ 'mtb__lp--sheet': linkSheet }"
            role="dialog"
            :aria-labelledby="linkTitleId"
            :data-sheet="linkSheet ? 'true' : 'false'"
            data-test="message-box-link-popover"
          >
            <div class="mtb__lp-head">
              <p :id="linkTitleId" class="mtb__lp-title" data-test="message-box-link-title">{{ linkTitle }}</p>
              <button type="button" class="mtb__lp-close" aria-label="Close" data-test="message-box-link-close" @click="closeLinkPicker(true)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div class="mtb__lp-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.2-4.2" /></svg>
              <input
                ref="lkSearch"
                type="text"
                class="mtb__lp-input"
                data-test="message-box-link-search"
                autocomplete="off"
                :value="linkQuery"
                :placeholder="linkSearchPlaceholder"
                :aria-label="linkSearchPlaceholder.replace('…', '')"
                @input="onLinkQuery"
                @keydown="onLinkKey"
              />
            </div>
            <div ref="lkList" role="listbox" class="mtb__lp-list" :aria-label="linkTitle" data-test="message-box-link-list">
              <div v-for="g in linkGroups" :key="g.key" class="mtb__lp-group" :data-group="g.key" data-test="message-box-link-group">
                <div class="mtb__lp-heading" data-test="message-box-link-heading">{{ g.heading }}</div>
                <button
                  v-for="o in g.items"
                  :key="o.item.kind + ':' + o.item.id"
                  type="button"
                  role="option"
                  class="mtb__lopt"
                  :class="{ 'is-hi': o.index === linkHi, 'is-selected': o.selected }"
                  :aria-selected="o.selected ? 'true' : 'false'"
                  :data-hi="o.index === linkHi ? '1' : '0'"
                  :data-kind="o.item.kind"
                  :data-id="o.item.id"
                  data-test="message-box-link-option"
                  @mousedown.prevent
                  @mouseenter="linkHi = o.index"
                  @click="pickLink(o.item)"
                >
                  <span class="mtb__lopt-ic" :class="{ 'is-packet': o.item.kind === 'packet' }" aria-hidden="true">
                    <svg v-if="o.item.kind === 'packet'" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7h-3a2 2 0 0 1-2-2V2" /><path d="M9 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h7l4 4v10a2 2 0 0 1-2 2Z" /><path d="M3 7.6v12.8A1.6 1.6 0 0 0 4.6 22h9.8" /></svg>
                    <svg v-else width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h8M8 17h5" /></svg>
                  </span>
                  <span class="mtb__lopt-text">
                    <span class="mtb__lopt-name" data-test="message-box-link-option-name">{{ o.item.name }}</span>
                    <span v-if="o.item.meta" class="mtb__lopt-meta">{{ o.item.meta }}</span>
                  </span>
                  <span v-if="o.item.tag" class="mtb__lopt-tag" data-test="message-box-link-option-tag">{{ o.item.tag }}</span>
                  <span class="mtb__lopt-check" aria-hidden="true">
                    <svg v-if="o.selected" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" data-test="message-box-link-check"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                </button>
              </div>
              <p v-if="linkItemsLoading" class="mtb__pop-note" data-test="message-box-link-loading">Loading your forms…</p>
              <p v-else-if="linkNoMatch" class="mtb__pop-note" data-test="message-box-link-none">{{ linkNoMatchText }}</p>
            </div>
            <div class="mtb__lp-foot" data-test="message-box-link-hint">↑↓ to move · Enter to pick · Esc to close</div>
          </div>
        </template>
      </span>

      <button
        v-for="chip in chips"
        :key="chip"
        type="button"
        class="mtb__chip"
        data-test="message-box-chip"
        :data-chip="chip"
        @mousedown.prevent
        @click="insertToken(chip)"
      >{{ token(chip) }}</button>

      <!-- The online-scheduling link (recall boxes of an office WITH online scheduling only; the page decides, see the
           `booking` prop). Not a variable chip: it looks different (link icon + words) and, besides inserting the token,
           asks which booking page the link should open. The popover is anchored to this chip. -->
      <span v-if="showBookingChip" ref="bkWrap" class="mtb__bk">
        <button
          type="button"
          class="mtb__chip mtb__chip--link"
          data-test="message-box-booking-chip"
          :data-chip="bookingVariable"
          aria-haspopup="dialog"
          :aria-expanded="bookingOpen ? 'true' : 'false'"
          @mousedown.prevent
          @click="onBookingChip"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
          Online scheduling link
        </button>
        <div v-if="bookingOpen" ref="bkPop" class="mtb__pop" role="dialog" :aria-labelledby="bookingTitleId" data-test="message-box-booking-popover" @keydown="onBookingKeydown">
          <p :id="bookingTitleId" class="mtb__pop-title">Which booking page should this link open?</p>
          <div role="radiogroup" :aria-labelledby="bookingTitleId" class="mtb__pop-list">
            <button
              type="button"
              role="radio"
              class="mtb__opt"
              :class="{ 'is-selected': bookingAptTypeId === null }"
              :aria-checked="bookingAptTypeId === null ? 'true' : 'false'"
              data-test="message-box-booking-option"
              data-apt-type=""
              @click="pickBookingPage(null)"
            >Your booking page (patient picks the visit)</button>
            <button
              v-for="type in bookingTypes"
              :key="type.id"
              type="button"
              role="radio"
              class="mtb__opt"
              :class="{ 'is-selected': bookingAptTypeId === type.id }"
              :aria-checked="bookingAptTypeId === type.id ? 'true' : 'false'"
              data-test="message-box-booking-option"
              :data-apt-type="type.id"
              @click="pickBookingPage(type)"
            >{{ type.name || type.id }}</button>
          </div>
          <p v-if="bookingTypesLoading" class="mtb__pop-note" data-test="message-box-booking-loading">Loading your appointment types…</p>
        </div>
      </span>
    </div>

    <!-- Under the box while the text holds the link: which booking page it opens, with Change (reopens the popover). A
         saved appointment type that is no longer bookable says what happens instead. -->
    <div v-if="showBookingReadout" class="mtb__bk-readout" data-test="message-box-booking-readout">
      <svg class="mtb__bk-readout-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
      <span>Link opens: <strong data-test="message-box-booking-label">{{ bookingReadoutLabel }}</strong></span>
      <span aria-hidden="true">&middot;</span>
      <button ref="bkChange" type="button" class="mtb__link" data-test="message-box-booking-change" @click="openBookingPicker">Change</button>
    </div>
    <div v-if="showBookingWarning" class="mtb__bk-readout mtb__bk-readout--warn" role="status" data-test="message-box-booking-warning">
      <svg class="mtb__bk-readout-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>
      <span>The appointment type this link opened was removed — it now opens your booking page.</span>
      <button ref="bkChange" type="button" class="mtb__link" data-test="message-box-booking-change" @click="openBookingPicker">Change</button>
    </div>

    <div v-if="locked" class="mtb__locked" data-test="message-box-locked">
      <svg class="mtb__locked-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
      <span>{{ lockedMessage }}</span>
    </div>

    <div v-if="showFoot" class="mtb__foot">
      <div v-if="isSms" class="mtb__meta" data-test="message-box-meta">
        <SmsGuidance :text="value" :vars="guidanceVars" :substitute-tags="true" />
      </div>
      <div v-else class="mtb__meta" data-test="message-box-signature-note">Your email signature is added automatically below this message — don’t paste it here.</div>
      <div class="mtb__actions">
        <button v-if="showReset" type="button" class="mtb__link" data-test="message-box-reset" @click="reset">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
          Reset to default
        </button>
        <button v-if="wikiLink" type="button" class="mtb__link" data-test="message-box-wiki-link" @click="$emit('wiki')">Use a wiki template instead</button>
      </div>
    </div>

    <div v-if="helper" class="mtb__helper" data-test="message-box-helper">{{ helper }}</div>
    <div v-if="note" class="mtb__helper" data-test="message-box-note">{{ note }}</div>

    <!-- EMAIL: what the patient's email looks like, with the sample patient. Closed by default so seven of these
         on one page stay short; the {{confirm_button}} variable is drawn as the exe's two buttons. Branding follows
         the sent email (teamio-utility lib/reminder-email-layout.js): an office WITH an email signature gets the
         signature placeholder ONLY (the layout appends the real one; no header, no footer), one WITHOUT gets the
         logo / name header and the practice name / address / phone footer (design frame 3a, plan §2.5a).
         Everything is interpolated text, never HTML. -->
    <div v-if="showPreviewToggle" class="mtb__preview">
      <button
        type="button"
        class="mtb__link mtb__pv-toggle"
        data-test="message-box-preview-toggle"
        :aria-expanded="previewOpen ? 'true' : 'false'"
        :aria-controls="previewId"
        @click="previewOpen = !previewOpen"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
        {{ previewOpen ? 'Hide preview' : 'Preview with sample patient' }}
      </button>
      <div v-if="previewOpen" :id="previewId" class="mtb__pv" data-test="message-box-preview">
        <div class="mtb__pv-top">
          <span class="mtb__eyebrow">Preview</span>
          <span class="mtb__pv-sample">Sample patient &middot; {{ sampleName }}</span>
        </div>
        <div class="mtb__pv-card">
          <div class="mtb__pv-subject">
            <span class="mtb__pv-subject-label">Subject:</span>
            <span class="mtb__pv-subject-text" data-test="message-box-preview-subject">{{ previewSubject }}</span>
          </div>
          <div v-if="!previewHasSignature" class="mtb__pv-brand">
            <img v-if="previewSite.logoUrl" :src="previewSite.logoUrl" :alt="previewName" class="mtb__pv-logo" data-test="message-box-preview-logo" />
            <template v-else>
              <span class="mtb__pv-initials" aria-hidden="true">{{ previewInitials }}</span>
              <span class="mtb__pv-name" data-test="message-box-preview-name">{{ previewName }}</span>
            </template>
          </div>
          <div class="mtb__pv-body" :class="{ 'mtb__pv-body--first': previewHasSignature }" data-test="message-box-preview-body">
            <template v-for="(block, i) in previewBlocks">
              <p v-if="block.type === 'p'" :key="i" class="mtb__pv-p">{{ block.text }}</p>
              <div v-else :key="i" class="mtb__pv-buttons" data-test="message-box-preview-buttons">
                <span class="mtb__pv-btn mtb__pv-btn--yes">Yes I'll be there</span>
                <span class="mtb__pv-btn mtb__pv-btn--no">I need to Cancel</span>
              </div>
            </template>
          </div>
          <div v-if="previewHasSignature" class="mtb__pv-sig" data-test="message-box-preview-signature">Your email signature appears here</div>
          <div v-else class="mtb__pv-foot" data-test="message-box-preview-footer">{{ previewFooter }}</div>
        </div>
      </div>
    </div>

    <div v-if="showSampleToggle" class="mtb__sample">
      <label class="mtb__switch" data-test="message-box-sample-toggle">
        <input v-model="sampleOn" type="checkbox" role="switch" class="mtb__switch-input" />
        <span class="mtb__switch-track" aria-hidden="true"><span class="mtb__switch-thumb"></span></span>
        <span>Preview with sample patient</span>
      </label>
      <div v-if="sampleOn" class="mtb__sample-card">
        <span class="mtb__eyebrow">Text to {{ sampleName }}</span>
        <div class="mtb__bubble" data-test="message-box-sample-bubble">{{ renderedSample }}</div>
      </div>
    </div>

    <!-- Spanish version: only when the office sends Spanish (the page decides). Collapsed by default so the page stays
         short; read-only with the English while the section is off; left out of a WhatsApp-only office's boxes. -->
    <div v-if="showSpanish" class="mtb__es" :class="{ 'is-open': spanishOpen, 'has-problem': spanishHasProblem }" data-test="message-box-spanish">
      <button
        type="button"
        class="mtb__es-head"
        data-test="message-box-spanish-toggle"
        :aria-expanded="spanishOpen ? 'true' : 'false'"
        :aria-controls="spanishBodyId"
        @click="spanishOpen = !spanishOpen"
      >
        <svg class="mtb__es-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20" /></svg>
        <span class="mtb__es-title">
          <span class="mtb__es-name">Spanish version</span>
          <span v-if="!spanishOpen" class="mtb__es-summary" data-test="message-box-spanish-summary">&middot; {{ spanishStatus }}</span>
          <span v-if="spanishOpen && spanishBadge" class="mtb__es-badge" :class="{ 'is-edited': spanishEdited }" data-test="message-box-spanish-badge">{{ spanishBadge }}</span>
        </span>
        <svg class="mtb__es-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path v-if="spanishOpen" d="m18 15-6-6-6 6" /><path v-else d="m6 9 6 6 6-6" /></svg>
      </button>

      <div v-show="spanishOpen" :id="spanishBodyId" class="mtb__es-body" data-test="message-box-spanish-body">
        <!-- Nothing to show yet (new or changed English, or a translation that did not come through): say when it appears
             instead of an empty box. -->
        <p v-if="spanishPending" class="mtb__es-pending" data-test="message-box-spanish-pending">Spanish is created automatically when you save.</p>
        <template v-else>
          <!-- EMAIL: the Spanish subject line, above the Spanish message. -->
          <template v-if="isEmail">
            <label class="mtb__eyebrow" :for="spanishSubjectId">Subject</label>
            <input
              :id="spanishSubjectId"
              ref="esSubject"
              type="text"
              class="mtb__subject mtb__subject--es"
              :class="{ 'is-invalid': !!spanishSubjectUnknown }"
              data-test="message-box-spanish-subject"
              lang="es"
              autocomplete="off"
              spellcheck="true"
              aria-label="Spanish version of the subject line"
              :value="spanishSubject"
              :readonly="locked"
              :aria-invalid="spanishSubjectUnknown ? 'true' : 'false'"
              @input="onSpanishSubjectInput"
            />
            <div v-if="spanishSubjectUnknown" class="mtb__msg" role="alert" data-test="message-box-spanish-subject-unknown">
              <svg class="mtb__msg-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
              <span>
                <code class="mtb__code">{{ spanishSubjectUnknownToken }}</code> {{ spanishSubjectUnknownBlurb }}<template v-if="spanishSubjectUnknownFixToken">
                  Did you mean
                  <button type="button" class="mtb__fix" title="Replace it" data-test="message-box-spanish-subject-fix" @click="applySpanishSubjectFix"><code class="mtb__code mtb__code--fix">{{ spanishSubjectUnknownFixToken }}</code></button>?</template>
              </span>
            </div>
            <label class="mtb__eyebrow mtb__eyebrow--message" :for="spanishFieldId">Message</label>
          </template>
          <textarea
            :id="spanishFieldId"
            ref="esField"
            class="mtb__field mtb__field--es"
            :class="{ 'is-invalid': !!spanishUnknown }"
            data-test="message-box-spanish-field"
            rows="3"
            lang="es"
            spellcheck="true"
            aria-label="Spanish version of this message"
            :value="spanishText"
            :readonly="locked"
            :aria-invalid="spanishUnknown ? 'true' : 'false'"
            @input="onSpanishInput"
          ></textarea>

          <div v-if="spanishUnknown" class="mtb__msg" role="alert" data-test="message-box-spanish-unknown">
            <svg class="mtb__msg-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
            <span>
              <code class="mtb__code">{{ spanishUnknownToken }}</code> {{ spanishUnknownBlurb }}<template v-if="spanishUnknownFixToken">
                Did you mean
                <button type="button" class="mtb__fix" title="Replace it" data-test="message-box-spanish-fix" @click="applySpanishFix"><code class="mtb__code mtb__code--fix">{{ spanishUnknownFixToken }}</code></button>?</template>
            </span>
          </div>
        </template>

        <div v-if="!spanishPending" class="mtb__es-foot">
          <span class="mtb__es-note" data-test="message-box-spanish-note">{{ spanishNote }}</span>
          <button v-if="canRetranslate" type="button" class="mtb__link mtb__es-retranslate" data-test="message-box-spanish-retranslate" @click="$emit('spanish-retranslate')">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
            Re-translate from English
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SmsGuidance from '@/components/SmsGuidance.vue';
import { getSmsGuidance } from '@/utils/smsGuidance';
import { findUnknownVariables } from '@/utils/templateVariables';
import { REMINDER_SAMPLE_VARS, BOOKING_LINK_SAMPLE_URL, BOOKING_LINK_PREVIEW_URL } from '@/utils/reminderSampleVars';
import { BOOKING_LINK_VARIABLE, hasBookingLinkToken } from '@/utils/reminderMessageDefaults';
import formLinkMixin from '@/components/MessageTemplateBoxFormLink';

const MIN_HEIGHT = 88;
const SINGLE_TEXT_GSM = 160;
const SINGLE_TEXT_UCS2 = 70;

// EMAIL preview: the layout drops a typed signature token (it appends the office signature itself) and draws
// {{confirm_button}} as the exe's Yes / Cancel buttons. Both also accept SendGrid's {{{triple}}} spelling.
const SIGNATURE_TOKEN = /\{\{\{?\s*email_signature_block\s*\}?\}\}/gi;
const CONFIRM_TOKEN = /\{\{\{?\s*confirm_button\s*\}?\}\}/i;

/**
 * MessageTemplateBox — one plain-text message the office edits in place (Appointment Reminders → Settings,
 * inline reminder messages, plan docs/plans/2026-10-17-inline-reminder-messages-plan.md §2.3 / §2.7).
 *
 * It replaces a "Template for …" wiki dropdown. The textarea's VALUE is the message, with the `{{variables}}`
 * the slot supports offered as chips under it (click = insert at the caret, into the box). Self-contained and
 * presentational: it holds no settings state, it only emits `input` (v-model compatible) and `wiki`.
 *
 * Two variants. SMS (`channel="sms"`, the default) is the message box described below. EMAIL (`channel="email"`,
 * plan §2.5a / design frames 2a-3a) adds, and changes, a few things; the SMS markup and behaviour do not move:
 *   - a Subject input above the message: `subject` / `defaultSubject` props, edited through `update:subject`; its own
 *     `subjectError` prop (the page sets it when Save refuses a blank subject) and the same unknown-{{variable}} flag
 *     with a "Did you mean" fix;
 *   - a variable chip inserts into the field used LAST (Subject or message; the message until one was used), except
 *     `confirm_button`, which only makes sense in the message;
 *   - no SMS counter, no credit line, no sample bubble. Instead the note "Your email signature is added automatically
 *     below this message", and a "Preview with sample patient" card (closed by default): the subject, the practice
 *     logo (`previewSite.logoUrl`) or name, the message as paragraphs, {{confirm_button}} drawn as the exe's two
 *     buttons (appended after the message on a confirmation box that has no token, like the layout does), then
 *     EITHER a signature placeholder (the office has an email signature: it is the only branding, so no logo / name
 *     above and no footer below) OR, when it has none, the logo / name above and the practice name / address / phone
 *     as the footer (`previewSite`: the page's own site row, the sample practice otherwise; `previewSite.hasSignature`
 *     === false means "no signature", anything else means "has one" - most offices do);
 *   - "Reset to default" and "Edited" cover the subject as well as the message;
 *   - the Spanish version has a Spanish subject above the Spanish message (`spanish.subject`, edited through
 *     `spanish-subject-input`);
 *   - it is never "informational": email still goes out at an office that has texting turned off.
 *
 * What it shows, all driven by props:
 *   - `defaultText`  → "Edited" + "Reset to default" appear only when the text differs from it;
 *   - `chips`        → variable names without braces (reminderMessageDefaults.js `chips`);
 *   - `helper`       → an optional one-line hint under the box (the recall single message has one);
 *   - `note`         → a one-off line from the page under the box (a wiki page it could not read);
 *   - `error`        → red outline + the message under the box (the page sets it when Save refuses);
 *   - `disabled`     → section switched off: read-only, muted fill, chips and links hidden (design 6a);
 *   - `informational`→ WhatsApp-only office: same, plus "Not in use" and no counter (design 6b);
 *   - `wikiLink`     → show "Use a wiki template instead" (emits `wiki`);
 *   - `spanish`      → the Spanish version sub-box (`{ enabled, text, edited, pending, englishChanged }`, see the prop):
 *                      collapsed by default under the English, an editable box with an "Auto-translated" / "Edited by
 *                      you" badge, "Re-translate from English" for hand-edited Spanish, or "Spanish is created
 *                      automatically when you save" while there is none to show. Emits `spanish-input` (the new text)
 *                      and `spanish-retranslate`; it holds no Spanish state of its own beyond open / closed.
 *
 * ONLINE-SCHEDULING LINK (recall boxes, plan docs/plans/2026-10-19-online-scheduling-link-token-plan.md section 3.4;
 * everything comes from the `booking` prop, so a box the page does not allow it in looks and behaves as before):
 *   - `booking.allowed`   → this box may hold {{online_scheduling_link}}: the unknown-{{variable}} check accepts the token
 *                           in the message and its Spanish (never in a subject), the counter measures it as a sample short
 *                           link and the previews draw it as a masked one;
 *   - `booking.available` → the office has online scheduling: only then does the "Online scheduling link" chip exist (it
 *                           inserts the token at the caret — in the message, never the subject — and opens a popover asking
 *                           which booking page it opens), and only then the "Link opens: … · Change" line under the box;
 *   - `booking.link` / `types` / `typesLoaded` / `typesLoading` → the saved choice ({ aptTypeId, label }; a null aptTypeId is
 *                           the general booking page), the appointment types a recall link may open, and whether that list is
 *                           the real one yet (a saved type missing from a LOADED list gets the "was removed" warning).
 *   The choice is handed back through `booking-link` ({ aptTypeId, label }); the box stores nothing.
 *
 * The counter and the credit line measure the message with a SAMPLE patient (`sampleVars`), because that is what
 * is actually sent; they say so (the ⓘ in <SmsGuidance>), as real names and dates change the length a little.
 * An unknown `{{variable}}` is flagged as you type with a one-click "Did you mean" fix; Save is blocked for it
 * by the page, not here.
 *
 * Hanken Grotesk on the root, with the app's global `a, .btn { font-family: Montserrat }` reset below.
 */
export default {
  name: 'MessageTemplateBox',
  components: { SmsGuidance },
  // The Form link chip (the generic `link` prop, Forms Settings): a mixin, so this file's own options stay what the reminders work pinned.
  mixins: [formLinkMixin],
  props: {
    value: { type: String, default: '' },
    label: { type: String, required: true },
    // The slot key (e.g. textConfirmTplForSingleApt): only used for data-slot / ids, so tests and the page can find a box.
    slotKey: { type: String, default: '' },
    channel: { type: String, default: 'sms', validator: v => v === 'sms' || v === 'email' },
    // EMAIL only: the subject line (v-model:subject — edited through `update:subject`), its default, and the problem
    // the page found with it on Save (a blank subject).
    subject: { type: String, default: '' },
    defaultSubject: { type: String, default: '' },
    subjectError: { type: String, default: '' },
    // EMAIL only: the practice the preview is drawn for — { name, logoUrl, address, phone, hasSignature }; whatever is
    // missing falls back to the sample practice (REMINDER_SAMPLE_VARS). hasSignature: false = the office has no email
    // signature, so the preview draws the logo / name header and the footer; anything else = it has one, so the preview
    // draws the signature placeholder only (the layout's rule).
    previewSite: { type: Object, default: () => ({}) },
    defaultText: { type: String, default: '' },
    chips: { type: Array, default: () => [] },
    helper: { type: String, default: '' },
    // A one-off line from the page under the box (e.g. "we couldn't read that wiki page, so this started as the default").
    note: { type: String, default: '' },
    error: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    informational: { type: Boolean, default: false },
    lockedNote: { type: String, default: '' },
    wikiLink: { type: Boolean, default: false },
    samplePreview: { type: Boolean, default: true },
    sampleVars: { type: Object, default: () => REMINDER_SAMPLE_VARS },
    // The Spanish version (plan §0 D7 / §2.3). The page decides all of it; the box only draws it:
    //   enabled  → the office sends Spanish, so the section exists (never in a WhatsApp-only office's boxes);
    //   text     → the Spanish to show ('' while there is none to show);
    //   edited   → the office changed the Spanish by hand (so it is never overwritten);
    //   pending  → no usable Spanish yet (new / changed English, or a translation that did not come through): the
    //              section says it is created when you save instead of showing an empty box;
    //   englishChanged → (edited only) the English changed after the office wrote the Spanish;
    //   subject → (EMAIL only) the Spanish subject line to show ('' while there is none).
    spanish: { type: Object, default: () => ({}) },
    // The online-scheduling link (see the header): { allowed, available, link: { aptTypeId, label }, types: [{ id, name }],
    // typesLoaded, typesLoading }. Absent/empty = this box has nothing to do with it.
    booking: { type: Object, default: () => ({}) },
  },
  data() {
    // lastField: which field a chip goes into (EMAIL): the one focused last; the message until one was used.
    return { sampleOn: false, spanishOpen: false, previewOpen: false, lastField: 'body', bookingOpen: false };
  },
  computed: {
    isSms() { return this.channel === 'sms'; },
    isEmail() { return this.channel === 'email'; },
    locked() { return this.disabled || this.informational; },
    fieldId() { return `mtb-${this._uid}`; },
    messageId() { return `mtb-${this._uid}-msg`; },
    subjectId() { return `mtb-${this._uid}-subject`; },
    subjectMessageId() { return `mtb-${this._uid}-subject-msg`; },
    previewId() { return `mtb-${this._uid}-preview`; },
    isBlank() { return !String(this.value || '').trim(); },
    unknown() {
      if (this.locked) return null;
      const found = findUnknownVariables(this.value, { allowBookingLink: this.bookingAllowed }).filter(u => !this.isOwnLinkToken(u));
      return found.length ? found[0] : null;
    },
    showUnknown() { return !!this.unknown; },
    // The message field has a problem (the page's error, or a variable we cannot fill in) / the subject field does.
    // On an SMS box only the message exists, so hasProblem is what it always was.
    bodyProblem() { return !!this.error || this.showUnknown; },
    subjectProblem() { return this.isEmail && (!!this.subjectError || this.showSubjectUnknown); },
    hasProblem() { return this.bodyProblem || this.subjectProblem; },

    // ── Subject (EMAIL) ──
    isSubjectBlank() { return !String(this.subject || '').trim(); },
    unknownSubject() {
      if (!this.isEmail || this.locked) return null;
      const found = findUnknownVariables(this.subject);
      return found.length ? found[0] : null;
    },
    showSubjectUnknown() { return !!this.unknownSubject; },
    subjectUnknownToken() { return this.unknownSubject ? this.token(this.unknownSubject.name) : ''; },
    subjectUnknownFixToken() {
      const u = this.unknownSubject;
      return u && u.suggestion ? this.token(u.suggestion) : '';
    },
    subjectUnknownBlurb() { return this.unknownBlurbFor(this.unknownSubject); },
    unknownToken() { return this.unknown ? this.token(this.unknown.name) : ''; },
    unknownFixToken() {
      const u = this.unknown;
      return u && u.suggestion ? this.token(u.suggestion) : '';
    },
    unknownBlurb() { return this.unknownBlurbFor(this.unknown); },
    edited() {
      return String(this.value || '') !== String(this.defaultText || '')
        || (this.isEmail && String(this.subject || '') !== String(this.defaultSubject || ''));
    },
    showEdited() { return this.edited && !this.locked && !this.hasProblem; },
    showReset() { return this.edited && !this.locked; },
    showChips() { return !this.locked && this.chips.length > 0; },
    showChipRow() { return this.showChips || this.showBookingChip || this.showLinkChip; },
    showFoot() { return !this.locked; },

    // ── Online-scheduling link ──
    bk() { return this.booking || {}; },
    bookingVariable() { return BOOKING_LINK_VARIABLE; },
    // This box may hold the token (a recall slot / recall-type rule): the unknown-variable check lets it through here only.
    bookingAllowed() { return this.bk.allowed === true; },
    // The chip: an allowed box of an office that has online scheduling, while the box is editable.
    showBookingChip() { return !this.locked && this.bookingAllowed && this.bk.available === true; },
    bookingTypes() { return Array.isArray(this.bk.types) ? this.bk.types : []; },
    bookingTypesLoaded() { return this.bk.typesLoaded === true; },
    bookingTypesLoading() { return this.bk.typesLoading === true; },
    // The saved choice; a missing / blank aptTypeId is the general booking page.
    bookingAptTypeId() {
      const id = this.bk.link && typeof this.bk.link.aptTypeId === 'string' ? this.bk.link.aptTypeId.trim() : '';
      return id === '' ? null : id;
    },
    bookingTitleId() { return `mtb-${this._uid}-bk-title`; },
    hasBookingToken() { return hasBookingLinkToken(this.value); },
    // A saved appointment type the (loaded) list no longer offers: deleted, hidden, or new-patients only. The sender
    // falls back to the general booking page; this says so. Never guessed before the list is known.
    bookingTypeRemoved() {
      return this.bookingAptTypeId !== null && this.bookingTypesLoaded && !this.bookingTypes.some(t => t.id === this.bookingAptTypeId);
    },
    showBookingReadout() { return this.showBookingChip && this.hasBookingToken && !this.bookingTypeRemoved; },
    showBookingWarning() { return this.showBookingChip && this.hasBookingToken && this.bookingTypeRemoved; },
    // The page's name for the type (a rename shows at once), else the label saved with the choice, else the general page.
    bookingReadoutLabel() {
      if (this.bookingAptTypeId === null) return 'Your booking page';
      const live = this.bookingTypes.find(t => t.id === this.bookingAptTypeId);
      const saved = this.bk.link && typeof this.bk.link.label === 'string' ? this.bk.link.label.trim() : '';
      return (live && live.name) || saved || 'Your booking page';
    },
    // Sample values: the counter measures the token as a real-length short link (plain ASCII: the estimate stays
    // GSM-7), the previews draw it masked. Only inside a box that may hold it.
    guidanceVars() {
      const base = this.bookingAllowed ? Object.assign({}, this.sampleVars, { [BOOKING_LINK_VARIABLE]: BOOKING_LINK_SAMPLE_URL }) : this.sampleVars;
      return this.linkActive ? Object.assign({}, base, { [this.linkToken]: this.linkSampleUrl }) : base;
    },
    bubbleVars() {
      const base = this.bookingAllowed ? Object.assign({}, this.sampleVars, { [BOOKING_LINK_VARIABLE]: BOOKING_LINK_PREVIEW_URL }) : this.sampleVars;
      return this.linkActive ? Object.assign({}, base, { [this.linkToken]: this.linkPreviewUrl }) : base;
    },
    showCounter() { return this.isSms && !this.informational; },
    showSampleToggle() { return this.isSms && !this.locked && this.samplePreview; },
    showPreviewToggle() { return this.isEmail && !this.locked; },
    rootClasses() {
      return { 'is-disabled': this.disabled, 'is-informational': this.informational, 'has-problem': this.hasProblem };
    },
    lockedMessage() {
      if (this.lockedNote) return this.lockedNote;
      return this.informational
        ? 'Not sent while texting is off. Kept here for when texting is turned back on.'
        : 'Read-only while this section is off. The message is still required, so it’s ready when you turn it on.';
    },
    guidance() {
      return getSmsGuidance(this.value || '', { vars: this.guidanceVars });
    },
    counterLimit() {
      return this.guidance.encoding === 'UCS-2' ? SINGLE_TEXT_UCS2 : SINGLE_TEXT_GSM;
    },
    overLimit() { return this.guidance.charCount > this.counterLimit; },
    counterText() { return `${this.guidance.charCount} / ${this.counterLimit}`; },
    sampleName() { return (this.sampleVars && this.sampleVars.patient_first_name) || 'a patient'; },
    renderedSample() {
      const vars = this.bubbleVars || {};
      return String(this.value || '').replace(/\{\{\s*([^{}]*?)\s*\}\}/g, (whole, name) => (
        Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : whole
      ));
    },

    // ── Email preview (sample patient) ──
    // The sample patient, with the practice's own name and phone when the page gave them (the logo, name, address and
    // phone around the message are the practice's, so the sentence inside it should agree).
    previewVars() {
      const vars = Object.assign({}, this.sampleVars || {});
      const site = this.previewSite || {};
      if (site.name) vars.site_name = site.name;
      if (site.phone) vars.site_officephone = site.phone;
      return vars;
    },
    // The message body's preview values: the same, plus the masked short link where the box may hold the token (a
    // subject never gets it — the token is not allowed there and is flagged).
    bodyPreviewVars() {
      const base = this.bookingAllowed ? Object.assign({}, this.previewVars, { [BOOKING_LINK_VARIABLE]: BOOKING_LINK_PREVIEW_URL }) : this.previewVars;
      return this.linkActive ? Object.assign({}, base, { [this.linkToken]: this.linkPreviewUrl }) : base;
    },
    // The sent email's branding rule: an office with an email signature shows the signature ONLY (no header, no
    // footer); only an explicit `hasSignature: false` brings the header + footer back.
    previewHasSignature() { return !(this.previewSite && this.previewSite.hasSignature === false); },
    previewName() { return (this.previewSite && this.previewSite.name) || (this.sampleVars && this.sampleVars.site_name) || 'Your practice'; },
    previewInitials() {
      const words = String(this.previewName).trim().split(/\s+/).filter(Boolean);
      return (words.length > 1 ? words[0].charAt(0) + words[1].charAt(0) : String(this.previewName).trim().slice(0, 2)).toUpperCase();
    },
    previewFooter() {
      const site = this.previewSite || {};
      const phone = site.phone || (this.sampleVars && this.sampleVars.site_officephone) || '';
      return [this.previewName, site.address || 'Your practice address', phone].filter(Boolean).join(' · ');
    },
    // The subject as the layout sends it: variables filled, newlines collapsed, trimmed; blank → the default subject.
    previewSubject() {
      const one = text => this.fillPreview(text).replace(/\s+/g, ' ').trim();
      return one(this.subject) || one(this.defaultSubject);
    },
    // A confirmation box (the only one that offers {{confirm_button}}) always ends up with the buttons: the layout
    // appends them when the office took the token out.
    isConfirmation() { return (this.chips || []).includes('confirm_button'); },
    // The message as the layout draws it: a blank line starts a new paragraph, a single line break stays one;
    // {{confirm_button}} becomes the two buttons where it stands; a typed signature token is dropped (the layout
    // appends the real signature).
    previewBlocks() {
      const body = String(this.value || '').replace(SIGNATURE_TOKEN, '');
      const parts = body.split(CONFIRM_TOKEN);
      const blocks = [];
      parts.forEach((part, i) => {
        this.fillPreview(part, this.bodyPreviewVars)
          .split(/\n\s*\n/)
          .map(text => text.replace(/^\n+|\n+$/g, ''))
          .filter(text => text.trim() !== '')
          .forEach(text => blocks.push({ type: 'p', text }));
        if (i < parts.length - 1) blocks.push({ type: 'buttons' });
      });
      if (parts.length === 1 && this.isConfirmation) blocks.push({ type: 'buttons' });
      return blocks;
    },

    // ── Spanish version ──
    sp() { return this.spanish || {}; },
    showSpanish() { return !!this.sp.enabled && !this.informational; },
    spanishText() { return typeof this.sp.text === 'string' ? this.sp.text : ''; },
    spanishSubject() { return typeof this.sp.subject === 'string' ? this.sp.subject : ''; },
    spanishSubjectId() { return `mtb-${this._uid}-es-subject`; },
    spanishEdited() { return !!this.sp.edited; },
    spanishPending() { return !!this.sp.pending; },
    spanishBodyId() { return `mtb-${this._uid}-es`; },
    spanishFieldId() { return `mtb-${this._uid}-es-field`; },
    spanishStatus() {
      if (this.spanishPending) return 'created when you save';
      return this.spanishEdited ? 'edited by you' : 'auto-translated';
    },
    // No badge while there is no Spanish yet.
    spanishBadge() {
      if (this.spanishPending) return '';
      return this.spanishEdited ? 'Edited by you' : 'Auto-translated';
    },
    // Re-translate undoes a hand edit, so it is offered only for Spanish the office changed (never while read-only).
    canRetranslate() { return this.spanishEdited && !this.locked; },
    spanishNote() {
      if (this.spanishEdited) {
        return this.sp.englishChanged
          ? 'English changed since you edited the Spanish.'
          : 'You changed this by hand, so it won’t update when you edit the English message.';
      }
      return 'Translated from your English message. Edit it if you’d like different wording.';
    },
    // The same unknown-{{variable}} check as the English, on the Spanish text, with its one-click fix.
    spanishUnknown() {
      if (this.locked || this.spanishPending) return null;
      const found = findUnknownVariables(this.spanishText, { allowBookingLink: this.bookingAllowed }).filter(u => !this.isOwnLinkToken(u));
      return found.length ? found[0] : null;
    },
    spanishUnknownToken() { return this.spanishUnknown ? this.token(this.spanishUnknown.name) : ''; },
    spanishUnknownFixToken() {
      const u = this.spanishUnknown;
      return u && u.suggestion ? this.token(u.suggestion) : '';
    },
    spanishUnknownBlurb() { return this.unknownBlurbFor(this.spanishUnknown); },
    // The same check on the Spanish SUBJECT (EMAIL), with its own one-click fix.
    spanishSubjectUnknown() {
      if (!this.isEmail || this.locked || this.spanishPending) return null;
      const found = findUnknownVariables(this.spanishSubject);
      return found.length ? found[0] : null;
    },
    spanishSubjectUnknownToken() { return this.spanishSubjectUnknown ? this.token(this.spanishSubjectUnknown.name) : ''; },
    spanishSubjectUnknownFixToken() {
      const u = this.spanishSubjectUnknown;
      return u && u.suggestion ? this.token(u.suggestion) : '';
    },
    spanishSubjectUnknownBlurb() { return this.unknownBlurbFor(this.spanishSubjectUnknown); },
    spanishHasProblem() { return !!this.spanishUnknown || !!this.spanishSubjectUnknown; },
  },
  watch: {
    value() { this.$nextTick(this.autoGrow); },
    spanishText() { this.$nextTick(this.autoGrow); },
    spanishPending() { this.$nextTick(this.autoGrow); },
    // A hidden textarea has no height: measure it again once it is on screen.
    spanishOpen() { this.$nextTick(this.autoGrow); },
    // Spanish with a variable we cannot fill in is never left hidden behind a collapsed header.
    spanishHasProblem: {
      immediate: true,
      handler(problem) { if (problem) this.spanishOpen = true; },
    },
    // The booking-page popover listens for Escape and clicks outside only while it is open, and never outlives the chip
    // (a section switched off, an office that lost online scheduling).
    bookingOpen(open) {
      if (open) {
        document.addEventListener('mousedown', this.onBookingOutside, true);
        document.addEventListener('keydown', this.onBookingEscape, true);
      } else {
        this.removeBookingListeners();
      }
    },
    showBookingChip(shown) { if (!shown) this.bookingOpen = false; },
  },
  mounted() {
    this.autoGrow();
    window.addEventListener('resize', this.autoGrow);
    // The web font arrives after the first paint and changes how the text wraps.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.autoGrow());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.autoGrow);
    this.removeBookingListeners();
  },
  methods: {
    token(name) { return `{{${name}}}`; },

    // Why the flagged {{variable}} cannot be filled (the same three sentences for the message, the subject and the Spanish).
    unknownBlurbFor(u) {
      if (!u) return '';
      if (u.reason === 'whitespace') return 'has extra spaces inside the braces.';
      if (u.reason === 'review-only') return 'only works in Reviews messages.';
      return 'isn’t something we can fill in.';
    },

    // The preview's {{variables}} filled with the sample patient; one it has no value for stays as typed.
    fillPreview(text, values) {
      const vars = values || this.previewVars;
      return String(text == null ? '' : text).replace(/\{\{\s*([^{}]*?)\s*\}\}/g, (whole, name) => (
        Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : whole
      ));
    },

    onInput(event) {
      this.rememberCaret();
      this.$emit('input', event.target.value);
    },

    onSubjectInput(event) {
      this.rememberSubjectCaret();
      this.$emit('update:subject', event.target.value);
    },

    onSpanishSubjectInput(event) {
      this.$emit('spanish-subject-input', event.target.value);
    },

    // The textarea grows with its text: no scrollbar, no resize handle, never shorter than three rows.
    autoGrow() {
      [this.$refs.field, this.$refs.esField].forEach((el) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(MIN_HEIGHT, el.scrollHeight + 2)}px`;
      });
    },

    onSpanishInput(event) {
      this.$emit('spanish-input', event.target.value);
    },

    // Swap every copy of the flagged token in the Spanish for its suggestion.
    applySpanishFix() {
      const u = this.spanishUnknown;
      if (!u || !u.suggestion) return;
      const from = `{{${u.name}}}`;
      this.$emit('spanish-input', this.spanishText.split(from).join(this.token(u.suggestion)));
    },

    // The same one-click fix on the Spanish subject (EMAIL).
    applySpanishSubjectFix() {
      const u = this.spanishSubjectUnknown;
      if (!u || !u.suggestion) return;
      const from = `{{${u.name}}}`;
      this.$emit('spanish-subject-input', this.spanishSubject.split(from).join(this.token(u.suggestion)));
    },

    // Where the caret was when focus last left the box, so a chip clicked later still inserts there.
    rememberCaret() {
      const el = this.$refs.field;
      if (!el) return;
      this.caret = { start: el.selectionStart, end: el.selectionEnd };
    },

    // The same for the Subject input (EMAIL).
    rememberSubjectCaret() {
      const el = this.$refs.subject;
      if (!el) return;
      this.subjectCaret = { start: el.selectionStart, end: el.selectionEnd };
    },

    // Insert {{name}} at the caret (the SendPlanModal.insertVar pattern). The chips use @mousedown.prevent, so
    // the click never moves focus out of the box and the live selection is still the caret. On an EMAIL box the
    // chip goes into the field used last (Subject or message); {{confirm_button}} and the online-scheduling link only ever
    // go into the message.
    insertToken(name, opts) {
      const toSubject = this.isEmail && this.lastField === 'subject' && name !== 'confirm_button' && name !== BOOKING_LINK_VARIABLE && !(this.linkActive && name === this.linkToken);
      const el = toSubject ? this.$refs.subject : this.$refs.field;
      const text = String((toSubject ? this.subject : this.value) || '');
      const focused = !!el && document.activeElement === el;
      const remembered = (toSubject ? this.subjectCaret : this.caret) || { start: text.length, end: text.length };
      const start = Math.min(focused ? el.selectionStart : remembered.start, text.length);
      const end = Math.min(focused ? el.selectionEnd : remembered.end, text.length);
      // `lead`: the Form link goes in with a space before it when it would otherwise touch the word before the caret.
      const lead = opts && opts.lead && start > 0 && !/\s$/.test(text.slice(0, start)) ? ' ' : '';
      const token = lead + this.token(name);
      const next = text.slice(0, start) + token + text.slice(end);
      const pos = start + token.length;
      this.$emit(toSubject ? 'update:subject' : 'input', next);
      this.$nextTick(() => {
        if (!el) return;
        el.focus({ preventScroll: true });
        el.setSelectionRange(pos, pos);
        if (toSubject) this.subjectCaret = { start: pos, end: pos };
        else this.caret = { start: pos, end: pos };
      });
    },

    // ── Online-scheduling link: the chip, its popover and the "Link opens" line ──

    // The chip: the token goes in at the caret (insertToken, as every chip does), then the popover asks which booking
    // page the link opens. The choice is the page's to store (`booking-link`); nothing is saved here.
    onBookingChip() {
      if (this.bookingOpen) { // the chip is also the popover's toggle: a second click closes it, nothing is inserted
        this.closeBookingPicker(true);
        return;
      }
      // ONE link per message: it has one booking-page choice. When the link is already there the chip just reopens the
      // choice (like "Change") instead of adding a second token.
      if (!this.hasBookingToken) this.insertToken(BOOKING_LINK_VARIABLE);
      this.openBookingPicker();
    },

    openBookingPicker() {
      this.bookingOpen = true;
      // After insertToken's own refocus of the message, so the popover keeps the focus (keyboard users land on the choice).
      this.$nextTick(() => this.focusBookingOption());
    },

    focusBookingOption() {
      const pop = this.$refs.bkPop;
      if (!pop) return;
      const target = pop.querySelector('[aria-checked="true"]') || pop.querySelector('[role="radio"]');
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    },

    pickBookingPage(type) {
      this.$emit('booking-link', { aptTypeId: type ? type.id : null, label: type ? (type.name || '') : '' });
      this.closeBookingPicker(true);
    },

    // `refocus`: Escape and a pick hand the focus back to the message (the caret as it was); a click elsewhere does not.
    closeBookingPicker(refocus) {
      if (!this.bookingOpen) return;
      this.bookingOpen = false;
      if (!refocus) return;
      this.$nextTick(() => {
        const el = this.$refs.field;
        if (!el) return;
        el.focus({ preventScroll: true });
        const len = String(this.value || '').length;
        const c = this.caret || { start: len, end: len };
        el.setSelectionRange(Math.min(c.start, len), Math.min(c.end, len));
      });
    },

    onBookingOutside(event) {
      const t = event.target;
      const inside = [this.$refs.bkWrap, this.$refs.bkChange].some(el => el && typeof el.contains === 'function' && el.contains(t));
      if (!inside) this.closeBookingPicker(false);
    },

    onBookingEscape(event) {
      if (event.key === 'Escape' || event.key === 'Esc') this.closeBookingPicker(true);
    },

    // Up / Down move between the choices (they are a radio group).
    onBookingKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const pop = this.$refs.bkPop;
      if (!pop) return;
      const options = Array.from(pop.querySelectorAll('[role="radio"]'));
      const at = options.indexOf(document.activeElement);
      if (at === -1 || !options.length) return;
      event.preventDefault();
      options[(at + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length].focus();
    },

    removeBookingListeners() {
      document.removeEventListener('mousedown', this.onBookingOutside, true);
      document.removeEventListener('keydown', this.onBookingEscape, true);
    },

    // Swap every copy of the flagged token for its suggestion.
    applyFix() {
      const u = this.unknown;
      if (!u || !u.suggestion) return;
      const from = `{{${u.name}}}`;
      this.$emit('input', String(this.value || '').split(from).join(this.token(u.suggestion)));
    },

    // …and in the subject (EMAIL).
    applySubjectFix() {
      const u = this.unknownSubject;
      if (!u || !u.suggestion) return;
      const from = `{{${u.name}}}`;
      this.$emit('update:subject', String(this.subject || '').split(from).join(this.token(u.suggestion)));
    },

    // "Reset to default" under the box puts back everything the box holds: the message, and on EMAIL the subject.
    reset() {
      this.$emit('input', this.defaultText);
      if (this.isEmail) this.$emit('update:subject', this.defaultSubject);
    },

    // The links inside a field's own error put back just that field.
    resetBody() {
      this.$emit('input', this.defaultText);
    },

    resetSubject() {
      this.$emit('update:subject', this.defaultSubject);
    },

    // `which` = 'subject' focuses the Subject input of an EMAIL box (the page uses it to land on a blank subject);
    // 'spanish' / 'spanish-subject' open the Spanish version and focus its message / subject.
    focus(which) {
      if (which === 'spanish' || which === 'spanish-subject') {
        this.spanishOpen = true;
        this.$nextTick(() => {
          const el = which === 'spanish-subject' && this.$refs.esSubject ? this.$refs.esSubject : this.$refs.esField;
          if (el) el.focus({ preventScroll: true });
        });
        return;
      }
      const el = which === 'subject' && this.$refs.subject ? this.$refs.subject : this.$refs.field;
      if (el) el.focus({ preventScroll: true });
    },
  },
};
</script>

<style lang="scss" scoped>
$mtb-font: 'Hanken Grotesk', 'Montserrat', system-ui, -apple-system, 'Segoe UI', sans-serif;
$mtb-mono: 'JetBrains Mono', ui-monospace, Menlo, Consolas, monospace;
$mtb-teal: #0f7259;
$mtb-teal-deep: #0b5c49;
$mtb-teal-subtle: #e9f6f1;
$mtb-teal-line: #74c3a8;
$mtb-ink: #0b0e11;
$mtb-muted: #717977;
$mtb-faint: #9ca4a2;
$mtb-border: #dfe4e3;
$mtb-fill-muted: #f4f6f5;
$mtb-error-line: #e5484d;
$mtb-error-text: #c62f35;
$mtb-warn-line: #f59e0b;
$mtb-warn-text: #8a5300;

.mtb {
  width: 100%;
  min-width: 0;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: $mtb-font;
  font-size: 14px;
  color: $mtb-ink;
  scroll-margin-top: 110px;

  /* The app's global stylesheet sets `a, .btn { font-family: Montserrat }`, which beats inheritance; reset it
     for everything drawn inside (including <SmsGuidance>'s own controls). */
  ::v-deep a,
  ::v-deep button,
  ::v-deep .btn {
    font-family: inherit;
  }
}

.mtb__head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 12px;
}

.mtb__label {
  flex: 1 1 220px;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: $mtb-ink;
}

.mtb__badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}

.mtb__edited {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: $mtb-muted;
}

.mtb__edited-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $mtb-teal;
}

.mtb__pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid $mtb-border;
  background: $mtb-fill-muted;
  font-size: 11.5px;
  font-weight: 600;
  color: #555d5b;
}

.mtb__counter {
  font-family: $mtb-mono;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid $mtb-border;
  background: #fff;
  color: #555d5b;

  &.is-over {
    border-color: $mtb-warn-line;
    background: #fef6e7;
    color: $mtb-warn-text;
  }
}

.mtb__field {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  padding: 11px 14px;
  font: 400 14px/1.55 $mtb-font;
  color: $mtb-ink;
  background: #fff;
  border: 1px solid $mtb-border;
  border-radius: 10px;
  outline: none;
  resize: none;
  overflow: hidden;
  white-space: pre-wrap;
  box-shadow: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: $mtb-teal;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.16);
  }
}

/* Each field outlines itself (is-invalid), so a bad Subject does not outline a fine message and the other way round. */
.mtb__field.is-invalid,
.mtb__subject.is-invalid {
  border-color: $mtb-error-line;
  box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.1);

  &:focus {
    border-color: $mtb-error-line;
    box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.18);
  }
}

.mtb.is-disabled .mtb__field,
.mtb.is-informational .mtb__field,
.mtb.is-disabled .mtb__subject {
  color: #555d5b;
  background: $mtb-fill-muted;
}

/* EMAIL: the Subject input and the small "Subject" / "Message" labels above the two fields (design frames 2a-3a). */
.mtb__sub {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mtb__eyebrow--message {
  margin-top: 6px;
}

.mtb__subject {
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  padding: 0 14px;
  font: 500 14px $mtb-font;
  color: $mtb-ink;
  background: #fff;
  border: 1px solid $mtb-border;
  border-radius: 10px;
  outline: none;
  box-shadow: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: $mtb-teal;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.16);
  }
}

.mtb__msg {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  color: $mtb-error-text;
}

.mtb__msg-icon {
  flex: none;
  margin-top: 1px;
  color: $mtb-error-line;
}

.mtb__msg-link {
  all: unset;
  cursor: pointer;
  font-weight: 700;
  text-decoration: underline;

  &:focus-visible {
    outline: 2px solid $mtb-error-line;
    outline-offset: 2px;
  }
}

.mtb__code {
  font-family: $mtb-mono;
  font-size: 12px;
  padding: 1px 5px;
  border-radius: 5px;
  background: #fdeeee;

  &--fix {
    background: $mtb-teal-subtle;
    color: $mtb-teal-deep;
    text-decoration: underline;
  }
}

.mtb__fix {
  all: unset;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $mtb-teal;
    outline-offset: 2px;
  }
}

.mtb__chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 2px;
}

.mtb__chips-label {
  margin-right: 2px;
  font-size: 12.5px;
  color: $mtb-muted;
}

.mtb__chip {
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  font-family: $mtb-mono;
  font-size: 12px;
  line-height: 1;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid $mtb-border;
  background: #fff;
  color: $mtb-teal-deep;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: $mtb-teal-subtle;
    border-color: $mtb-teal-line;
  }

  &:active {
    background: $mtb-teal;
    border-color: $mtb-teal;
    color: #fff;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.16);
  }
}

/* The online-scheduling link chip: a link icon and plain words (not a monospace {{variable}}), tinted, so it reads as a
   different kind of chip; the popover hangs under it. */
.mtb__chip--link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: $mtb-font;
  font-size: 12.5px;
  font-weight: 600;
  background: $mtb-teal-subtle;
  border-color: $mtb-teal-line;
}

.mtb__bk {
  position: relative;
  display: inline-block;
}

/* ── The Form link chip + its searchable popover (a bottom sheet at phone width). The chip reuses .mtb__chip--link above. ── */
.mtb__chip-req {
  font-weight: 500;
  color: $mtb-muted;
}

.mtb__chip--link.is-missing {
  border-color: $mtb-error-line;

  .mtb__chip-req {
    color: $mtb-error-text;
  }
}

.mtb__lp {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 40;
  box-sizing: border-box;
  width: 400px;
  max-width: calc(100vw - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid $mtb-border;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(11, 14, 17, 0.16);
}

.mtb__lp-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 12px 10px 16px;
}

.mtb__lp-title {
  flex: 1;
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: $mtb-ink;
}

.mtb__lp-close {
  all: unset;
  box-sizing: border-box;
  flex: none;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $mtb-muted;

  &:hover {
    background: $mtb-fill-muted;
  }

  &:focus-visible {
    outline: 2px solid $mtb-teal;
    outline-offset: 1px;
  }
}

.mtb__lp-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  margin: 0 12px 10px;
  padding: 0 12px;
  border: 1px solid $mtb-teal;
  border-radius: 10px;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.16);
  color: $mtb-faint;
}

.mtb__lp-input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: $mtb-ink;
}

.mtb__lp-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 0 6px 8px;
  border-top: 1px solid $mtb-border;
}

.mtb__lp-heading {
  padding: 10px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $mtb-muted;
}

.mtb__lopt {
  all: unset;
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-family: $mtb-font;

  &.is-hi {
    background: $mtb-fill-muted;
  }

  &.is-selected {
    background: $mtb-teal-subtle;
  }

  &:focus-visible {
    outline: 2px solid $mtb-teal;
    outline-offset: -2px;
  }
}

.mtb__lopt-ic {
  display: flex;
  flex: none;
  color: $mtb-faint;

  &.is-packet {
    color: $mtb-teal;
  }
}

.mtb__lopt-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.mtb__lopt-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  color: $mtb-ink;

  .is-selected & {
    font-weight: 700;
  }
}

.mtb__lopt-meta {
  font-size: 12.5px;
  color: $mtb-muted;
}

.mtb__lopt-tag {
  flex: none;
  padding: 2px 8px;
  border-radius: 999px;
  background: $mtb-fill-muted;
  font-size: 11.5px;
  font-weight: 600;
  color: #555d5b;
  white-space: nowrap;
}

.mtb__lopt-check {
  display: flex;
  flex: none;
  width: 16px;
  color: $mtb-teal;
}

.mtb__lp-foot {
  padding: 9px 16px;
  border-top: 1px solid $mtb-border;
  background: #fafbfb;
  font-size: 12px;
  color: $mtb-muted;
}

.mtb__lp-scrim {
  position: fixed;
  inset: 0;
  z-index: 39;
  background: rgba(12, 15, 14, 0.42);
}

/* PHONE: the picker is a bottom sheet with 52px rows (board 4c). Fixed to the viewport, above the scrim. */
.mtb__lp--sheet {
  position: fixed;
  top: auto;
  left: 0;
  right: 0;
  bottom: 0;
  width: auto;
  max-width: none;
  max-height: 80vh;
  border: 0;
  border-radius: 18px 18px 0 0;

  .mtb__lp-head {
    padding: 14px 10px 10px 18px;
  }

  .mtb__lp-title {
    font-size: 16px;
  }

  .mtb__lp-close {
    width: 44px;
    height: 44px;
    border-radius: 10px;
  }

  .mtb__lp-search {
    height: 44px;
    margin: 0 14px 10px;
    box-shadow: none;
    border-color: $mtb-border;
  }

  .mtb__lp-input {
    font-size: 16px; /* iOS Safari zooms in on a focused field below 16px */
  }

  .mtb__lp-list {
    flex: 1;
    min-height: 0;
    max-height: none;
    padding: 0 8px 18px;
  }

  .mtb__lopt {
    min-height: 52px;
    gap: 12px;
    border-radius: 10px;
  }

  .mtb__lopt-name {
    font-size: 15px;
  }

  .mtb__lp-foot {
    display: none;
  }
}

.mtb__bk-readout--none {
  strong {
    color: $mtb-warn-text;
  }
}

.mtb__pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  box-sizing: border-box;
  width: 320px;
  max-width: calc(100vw - 48px);
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
  background: #fff;
  border: 1px solid $mtb-border;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(11, 14, 17, 0.14);
}

.mtb__pop-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: $mtb-ink;
}

.mtb__pop-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mtb__opt {
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: $mtb-font;
  font-size: 13.5px;
  line-height: 1.4;
  color: $mtb-ink;

  &:hover {
    background: $mtb-fill-muted;
  }

  &.is-selected {
    border-color: $mtb-teal-line;
    background: $mtb-teal-subtle;
    color: $mtb-teal-deep;
    font-weight: 600;
  }

  &:focus-visible {
    outline: 2px solid $mtb-teal;
    outline-offset: 1px;
  }
}

.mtb__pop-note {
  margin: 8px 0 0;
  font-size: 12.5px;
  color: $mtb-muted;
}

/* "Link opens: … · Change" under the box (and the warning that replaces it). */
.mtb__bk-readout {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 13px;
  line-height: 1.45;
  color: $mtb-muted;

  strong {
    color: $mtb-ink;
    font-weight: 600;
  }

  &--warn {
    color: $mtb-warn-text;
  }
}

.mtb__bk-readout-icon {
  flex: none;
  color: $mtb-teal;
}

.mtb__bk-readout--warn .mtb__bk-readout-icon {
  color: $mtb-warn-line;
}

.mtb__locked {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: $mtb-muted;
}

.mtb__locked-icon {
  flex: none;
  color: $mtb-faint;
}

.mtb__foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 16px;
}

.mtb__meta {
  flex: 1 1 260px;
  min-width: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: $mtb-muted;

  /* The shared <SmsGuidance> line (characters, credits, credits left, the ⓘ) in this page's type size. */
  ::v-deep .sms-guidance {
    font-size: 12.5px;
  }
}

.mtb__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px 16px;
  margin-left: auto;
}

.mtb__link {
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: $mtb-teal;

  &:hover {
    color: $mtb-teal-deep;
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $mtb-teal;
    outline-offset: 2px;
  }
}

.mtb__helper {
  font-size: 12.5px;
  line-height: 1.45;
  color: $mtb-muted;
}

.mtb__sample {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 2px;
}

.mtb__switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  align-self: flex-start;
  margin: 0;
  font-size: 13.5px;
  cursor: pointer;
}

.mtb__switch-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.mtb__switch-track {
  position: relative;
  flex: none;
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: #c9d0ce;
  transition: background 0.15s;
}

.mtb__switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s;
}

.mtb__switch-input:checked + .mtb__switch-track {
  background: $mtb-teal;
}

.mtb__switch-input:checked + .mtb__switch-track .mtb__switch-thumb {
  transform: translateX(16px);
}

.mtb__switch-input:focus-visible + .mtb__switch-track {
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.16);
}

.mtb__sample-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px;
  border-radius: 12px;
  background: $mtb-fill-muted;
}

.mtb__eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $mtb-muted;
}

.mtb__bubble {
  max-width: 420px;
  padding: 10px 13px;
  border-radius: 16px 16px 16px 4px;
  background: #fff;
  border: 1px solid $mtb-border;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  color: $mtb-ink;
}

/* Spanish version: a quiet panel under the English message (design frames 4a-4c). */
.mtb__es {
  margin-top: 6px;
  border: 1px solid $mtb-border;
  border-radius: 12px;
  background: #fafbfb;
  overflow: hidden;
}

.mtb__es-head {
  all: unset;
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;

  &:hover {
    background: $mtb-fill-muted;
  }

  &:focus-visible {
    outline: 2px solid $mtb-teal;
    outline-offset: -2px;
  }
}

.mtb__es-icon,
.mtb__es-chev {
  flex: none;
  color: $mtb-muted;
}

.mtb__es-title {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}

.mtb__es-name {
  font-size: 13.5px;
  font-weight: 700;
}

.mtb__es-summary {
  font-size: 13px;
  color: $mtb-muted;
}

.mtb__es-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  background: $mtb-teal-subtle;
  color: $mtb-teal-deep;

  &.is-edited {
    background: #fef6e7;
    color: $mtb-warn-text;
  }
}

.mtb__es-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px 14px 14px;
}

.mtb__es-pending {
  margin: 0;
  padding: 11px 14px;
  border: 1px dashed $mtb-border;
  border-radius: 10px;
  background: #fff;
  font-size: 13.5px;
  line-height: 1.5;
  color: $mtb-muted;
}

.mtb__es-foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 16px;
}

.mtb__es-note {
  flex: 1 1 240px;
  min-width: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: $mtb-muted;
}

.mtb__es-retranslate {
  margin-left: auto;
}

/* EMAIL preview: a quiet card with the message as the patient will see it (design frame 3a). Closed by default. */
.mtb__preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding-top: 2px;
}

.mtb__pv {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mtb__pv-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}

.mtb__pv-sample {
  font-size: 12.5px;
  color: $mtb-faint;
}

.mtb__pv-card {
  background: #fff;
  border: 1px solid $mtb-border;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(11, 14, 17, 0.05);
}

.mtb__pv-subject {
  display: flex;
  gap: 6px;
  min-width: 0;
  padding: 10px 16px;
  border-bottom: 1px solid #eaeeed;
  font-size: 12.5px;
  color: $mtb-muted;
}

.mtb__pv-subject-label {
  flex: none;
}

.mtb__pv-subject-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: $mtb-ink;
}

.mtb__pv-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 18px 18px 4px;
}

/* Practice logo: height-capped in this file (repo-wide logo guardrail). */
.mtb__pv-logo {
  display: block;
  max-height: 56px;
  max-width: 200px;
  object-fit: contain;
}

.mtb__pv-initials {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: $mtb-teal-subtle;
  color: $mtb-teal-deep;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
}

.mtb__pv-name {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.mtb__pv-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 18px 4px;
  font-size: 13.5px;
  line-height: 1.55;
  color: #232931;
}

/* No header above the message (the office's signature is the branding): a little more room under the subject. */
.mtb__pv-body--first {
  padding-top: 16px;
}

.mtb__pv-p {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.mtb__pv-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
}

.mtb__pv-btn {
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;

  &--yes {
    background: $mtb-teal;
    color: #fff;
  }

  &--no {
    border: 1px solid #c9d0ce;
    color: #3d4442;
  }
}

.mtb__pv-sig {
  margin: 10px 18px 16px;
  padding: 10px 12px;
  border: 1px dashed #c9d0ce;
  border-radius: 8px;
  font-size: 12px;
  color: $mtb-muted;
}

.mtb__pv-foot {
  padding: 12px 18px;
  background: #fafbfb;
  border-top: 1px solid #eaeeed;
  font-size: 11.5px;
  line-height: 1.5;
  color: $mtb-muted;
  overflow-wrap: anywhere;
}
</style>
